/**
 * What the firm owes the driver (card and account fares, and card tips: they already
 * have cash fares), what's made it up since they were last paid, and their settlements.
 */
import { errorMessage, get } from "@taxi/mobile-web/api";
import { money, when } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { WalletIcon } from "@taxi/mobile-web/icons";
import { navigate } from "@taxi/mobile-web/router";
import { Card, Empty, Loading, Notice, Row, TopBar } from "@taxi/mobile-web/ui";
import { shortDate } from "../components/parts";
import type { BalanceDetail, LedgerEntry, Settlement, SettlementDetail } from "../lib/types";

const METHODS: Record<Settlement["method"], string> = {
  bank_transfer: "Bank transfer",
  cash: "Cash",
  other: "Paid",
};

const COVERS: Record<Settlement["covers"], string> = {
  fares: "Fares",
  tips: "Tips",
  both: "Fares and tips",
};

function Entries({ entries }: { entries: LedgerEntry[] }) {
  return (
    <ul className="ledger">
      {entries.map((entry) => (
        <li key={entry.id}>
          <span className="ledger-what">
            {entry.description}
            <small>{when(new Date(entry.occurredAt))}</small>
          </span>
          <span className={`ledger-amount${entry.amountPence < 0 ? " ledger-minus" : ""}`}>
            {entry.amountPence < 0 ? "−" : ""}
            {money(Math.abs(entry.amountPence))}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function MoneyScreen() {
  const balance = useResource(() => get<BalanceDetail>("/driver/balance"), []);
  const settlements = useResource(() => get<{ data: Settlement[] }>("/driver/settlements"), []);

  if (balance.error) {
    return (
      <div className="money">
        <TopBar title="Money" />
        <div className="screen-body">
          <Notice tone="error">{errorMessage(balance.error)}</Notice>
        </div>
      </div>
    );
  }
  const b = balance.data;
  return (
    <div className="money">
      <TopBar title="Money" />
      {!b ? (
        <Loading />
      ) : (
        <div className="screen-body">
          <section className="balance balance-hero">
            <span className="overline">The firm owes you</span>
            <strong>{money(b.totalPence)}</strong>
            {b.lastSettlement && (
              <span className="balance-last">
                Last paid {money(b.lastSettlement.totalPence)} on{" "}
                {shortDate(b.lastSettlement.paidOn)}
              </span>
            )}
            <dl className="balance-split">
              <div>
                <dt>Card and account fares</dt>
                <dd>{money(b.faresPence)}</dd>
              </div>
              <div>
                <dt>Tips</dt>
                <dd>{money(b.tipPotPence)}</dd>
              </div>
            </dl>
          </section>
          <p className="fine-print">
            Cash fares aren't in it: you already have that money. The office pays you what's owed
            and records it here.
          </p>

          <h2 className="section-title">Since you were last paid</h2>
          {b.entries.length === 0 ? (
            <p className="muted">Nothing yet.</p>
          ) : (
            <Entries entries={b.entries} />
          )}

          <h2 className="section-title">Payments to you</h2>
          {settlements.data && settlements.data.data.length === 0 && (
            <p className="muted">None yet.</p>
          )}
          {settlements.data && settlements.data.data.length > 0 && (
            <div className="row-group">
              {settlements.data.data.map((s) => (
                <Row
                  key={s.id}
                  icon={<WalletIcon />}
                  title={`${money(s.totalPence)} · ${METHODS[s.method]}`}
                  subtitle={`${shortDate(s.paidOn)} · ${COVERS[s.covers]}`}
                  onClick={() => navigate(`/money/settlements/${s.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SettlementScreen({ id }: { id: string }) {
  const settlement = useResource(() => get<SettlementDetail>(`/driver/settlements/${id}`), [id]);
  const s = settlement.data;
  return (
    <div className="money">
      <TopBar title={s ? `Payment ${s.number}` : "Payment"} back="/money" />
      {settlement.error ? (
        <Empty title="Can't show this payment">
          <p>{errorMessage(settlement.error)}</p>
        </Empty>
      ) : !s ? (
        <Loading />
      ) : (
        <div className="screen-body">
          <Card className="balance">
            <span className="overline">
              {METHODS[s.method]} on {shortDate(s.paidOn)}
            </span>
            <strong>{money(s.totalPence)}</strong>
            <dl className="balance-split">
              {s.covers !== "tips" && (
                <div>
                  <dt>Fares</dt>
                  <dd>{money(s.faresPence)}</dd>
                </div>
              )}
              {s.covers !== "fares" && (
                <div>
                  <dt>Tips</dt>
                  <dd>{money(s.tipsPence)}</dd>
                </div>
              )}
              {s.adjustmentsPence !== 0 && (
                <div>
                  <dt>Adjustments</dt>
                  <dd>
                    {s.adjustmentsPence < 0 ? "−" : ""}
                    {money(Math.abs(s.adjustmentsPence))}
                  </dd>
                </div>
              )}
            </dl>
            {s.reference && <p className="fine-print">Reference: {s.reference}</p>}
            {s.notes && <p className="fine-print">{s.notes}</p>}
          </Card>
          <h2 className="section-title">What it covered</h2>
          {s.entries.length === 0 ? (
            <p className="muted">No entries.</p>
          ) : (
            <Entries entries={s.entries} />
          )}
        </div>
      )}
    </div>
  );
}
